import React, { memo, useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import {
  ArrowIcon,
  ErrorFilledIcon,
  SuccessFilledIcon,
  WarningTriangleIcon,
} from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import {
  ReadinessCheckSeverity,
  ReadinessCheckStatus,
} from '../../../../../readiness/readinessChecks.constants';
import { ReadinessStepTestIds } from '../../ReadinessStep.testIds';

import {
  contentStyles,
  descriptionStyles,
  fixButtonStyles,
  getMarkColor,
  getRowStyles,
  markStyles,
  titleRowStyles,
} from './ReadinessCheckRow.styles';

const getMarkIcon = ({ status, severity }) => {
  if (status === ReadinessCheckStatus.PASS) {
    return SuccessFilledIcon;
  }

  if (severity === ReadinessCheckSeverity.HARD) {
    return ErrorFilledIcon;
  }

  return WarningTriangleIcon;
};

const ReadinessCheckRow = ({ check, isHighlighted, onFix }) => {
  const { id, status, severity, title, description, chip, deepLink } = check;
  const MarkIcon = getMarkIcon({ status, severity });
  const showFix = !!deepLink && status !== ReadinessCheckStatus.PASS;

  const handleFix = useCallback(() => onFix(deepLink), [onFix, deepLink]);

  return (
    <Box data-testid={ReadinessStepTestIds.getCheckRowId(id)} sx={getRowStyles(isHighlighted)}>
      <Box sx={markStyles(getMarkColor({ status, severity }))}>
        <MarkIcon size={IconSize[20]} />
      </Box>
      <Box sx={contentStyles}>
        <Box sx={titleRowStyles}>
          <Typography variant="bold14">{title}</Typography>
          {chip && <LabelCDS size="S" title={chip.label} variant={chip.variant} />}
        </Box>
        <Typography sx={descriptionStyles} variant="regular12">
          {description}
        </Typography>
      </Box>
      {showFix && (
        <Button
          data-testid={ReadinessStepTestIds.getCheckFixId(id)}
          sx={fixButtonStyles}
          variant="text"
          onClick={handleFix}
        >
          <Typography variant="bold12">Fix</Typography>
          <ArrowIcon direction={IconDirection.RIGHT} size={IconSize[14]} />
        </Button>
      )}
    </Box>
  );
};

ReadinessCheckRow.propTypes = {
  check: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    severity: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    chip: PropTypes.shape({
      label: PropTypes.string.isRequired,
      variant: PropTypes.string.isRequired,
    }),
    deepLink: PropTypes.shape({
      tab: PropTypes.string.isRequired,
    }),
  }).isRequired,
  onFix: PropTypes.func.isRequired,
  isHighlighted: PropTypes.bool,
};

export default memo(ReadinessCheckRow);
